import { useEffect, useRef, useState } from 'react';
import { api, errorText } from '../lib/api';
import { classNames, fmtDateTime, splitMarks, useDebounced } from '../lib/util';

const KINDS: [string, string][] = [
  ['entry', 'Notes'],
  ['note_item', 'Organized notes'],
  ['entity', 'Records'],
  ['claim', 'Claims'],
  ['chunk', 'Documents'],
];

export function SearchView({ campaignId }: { campaignId: string }) {
  const [q, setQ] = useState('');
  const [kinds, setKinds] = useState<string[]>([]);
  const [session, setSession] = useState('');
  const [labels, setLabels] = useState('');
  const [playerSafe, setPlayerSafe] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [hits, setHits] = useState<any[] | null>(null);
  const [err, setErr] = useState('');
  const dq = useDebounced(q, 250);
  useEffect(() => {
    api(`/api/campaigns/${campaignId}/dashboard`)
      .then((d: any) => setSessions(d.sessions))
      .catch(() => undefined);
  }, [campaignId]);
  useEffect(() => {
    if (!dq.trim()) return setHits(null);
    const p = new URLSearchParams({ q: dq, kinds: kinds.join(','), labels, session, playerSafe: playerSafe ? '1' : '0' });
    api<any[]>(`/api/campaigns/${campaignId}/search?${p}`)
      .then((h) => {
        setHits(h);
        setErr('');
      })
      .catch((e) => setErr(errorText(e)));
  }, [dq, kinds, session, labels, playerSafe, campaignId]);
  const link = (h: any) => {
    if (h.kind === 'entity') return `#/c/${campaignId}/records/e/${h.refId}`;
    if (h.kind === 'chunk') return `#/c/${campaignId}/documents/${h.document_id}?page=${h.page_index}`;
    if (h.session_id) return `#/c/${campaignId}/s/${h.session_id}`;
    return undefined;
  };
  return (
    <div className="page">
      <h1>Search & ask</h1>
      <Ask campaignId={campaignId} />
      <section className="card">
        <h2>Keyword search</h2>
        <p className="muted small">Works without the AI service.</p>
        <input className="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes, records and documents" aria-label="Search" type="search" />
        <div className="filters">
          {KINDS.map(([k, label]) => (
            <label key={k} className="check">
              <input type="checkbox" checked={kinds.includes(k)} onChange={() => setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k])} /> {label}
            </label>
          ))}
          <label>
            Session{' '}
            <select value={session} onChange={(e) => setSession(e.target.value)}>
              <option value="">Any</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  Session {s.number}
                  {s.title ? ` — ${s.title}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            Labels{' '}
            <input value={labels} onChange={(e) => setLabels(e.target.value)} placeholder="e.g. rumor, npc, #loot" size={16} />
          </label>
          <label className="check">
            <input type="checkbox" checked={playerSafe} onChange={(e) => setPlayerSafe(e.target.checked)} /> Player-safe only
          </label>
        </div>
        {err && <p className="error">{err}</p>}
        {hits && hits.length === 0 && <p className="muted">No matches.</p>}
        {hits && hits.length > 0 && (
          <ul className="hits">
            {hits.map((h) => (
              <li key={`${h.kind}:${h.refId}`}>
                <a href={link(h)} className="hit">
                  <span className="tag subtle">{KINDS.find(([k]) => k === h.kind)?.[1]}</span>
                  {h.kind === 'entry' && <span className="muted small">Session {h.session_number} · {fmtDateTime(h.captured_at)}</span>}
                  {h.kind === 'chunk' && (
                    <span className="muted small">
                      {h.document_title} · PDF page {h.page_index + 1}
                      {h.page_label && h.page_label !== String(h.page_index + 1) ? ` (printed ${h.page_label})` : ''}
                    </span>
                  )}
                  {h.kind === 'entity' && <span className="muted small">{h.title}</span>}
                  {h.kind === 'claim' && <span className="muted small">{h.kind} · {h.status}</span>}
                  {h.visibility === 'gm_only' && <span className="tag secret">GM only</span>}
                  <span className="snippet">
                    {splitMarks(h.snippet).map((p, i) => (p.mark ? <mark key={i}>{p.text}</mark> : <span key={i}>{p.text}</span>))}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const PART_LABEL: Record<string, string> = {
  supported: 'Supported by notes',
  interpretation: 'Interpretation',
  conflict: 'Unresolved conflict',
  missing: 'Not in your notes',
  published_rule: 'Published rule',
  house_rule: 'House rule',
  not_supported: 'Not supported by imported sources',
};

function Ask({ campaignId }: { campaignId: string }) {
  const [question, setQuestion] = useState('');
  const [mode, setMode] = useState<'history' | 'rules'>('history');
  const [playerSafe, setPlayerSafe] = useState(false);
  const [current, setCurrent] = useState<any>(null);
  const [recent, setRecent] = useState<any[]>([]);
  const [err, setErr] = useState('');
  const poll = useRef<number>(0);
  const loadRecent = () =>
    api<any[]>(`/api/campaigns/${campaignId}/questions`)
      .then(setRecent)
      .catch(() => undefined);
  useEffect(() => {
    void loadRecent();
    return () => clearTimeout(poll.current);
  }, [campaignId]);
  const watch = (id: string) => {
    clearTimeout(poll.current);
    const tick = async () => {
      try {
        const q = await api(`/api/questions/${id}`);
        setCurrent(q);
        if (q.status === 'queued') poll.current = window.setTimeout(tick, 1500);
        else void loadRecent();
      } catch (e) {
        setErr(errorText(e));
      }
    };
    void tick();
  };
  const ask = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    try {
      const q = await api(`/api/campaigns/${campaignId}/questions`, { body: { question, mode, playerSafe } });
      setCurrent(q);
      watch(q.id);
    } catch (x) {
      setErr(errorText(x));
    }
  };
  return (
    <section className="card">
      <h2>Ask about the campaign</h2>
      <form onSubmit={ask} className="ask-form">
        <div className="row wrap">
          <label className="check">
            <input type="radio" name="mode" checked={mode === 'history'} onChange={() => setMode('history')} /> Campaign history
          </label>
          <label className="check">
            <input type="radio" name="mode" checked={mode === 'rules'} onChange={() => setMode('rules')} /> Rules (this campaign’s system & edition)
          </label>
          <label className="check">
            <input type="checkbox" checked={playerSafe} onChange={(e) => setPlayerSafe(e.target.checked)} /> Player-safe
          </label>
        </div>
        <div className="row">
          <input className="grow" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={mode === 'rules' ? 'e.g. How does grappling work?' : 'e.g. What do we know about the widow?'} aria-label="Question" required />
          <button className="btn primary">Ask</button>
        </div>
      </form>
      {err && <p className="error">{err}</p>}
      {current && <Answer q={current} />}
      {recent.length > 0 && (
        <details>
          <summary>Recent questions</summary>
          <ul className="plain">
            {recent.map((r) => (
              <li key={r.id}>
                <button className="link" onClick={() => watch(r.id)}>
                  {r.question}
                </button>{' '}
                <span className="muted small">
                  {r.mode} · {r.status}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function Answer({ q }: { q: any }) {
  if (q.status === 'queued') return <p className="muted">Waiting for the AI service… (questions are kept and answered when it is available)</p>;
  if (q.status === 'failed') return <p className="error">The question could not be answered: {q.error}</p>;
  const a = q.answer;
  if (!a) return null;
  return (
    <div className="answer">
      <p className="muted small">
        Q: {q.question} {q.player_safe ? '· player-safe' : ''} {a.model ? `· ${a.model}` : ''}
      </p>
      {a.parts.map((p: any, i: number) => (
        <div key={i} className={classNames('answer-part', `part-${p.type}`)}>
          <span className="tag">{PART_LABEL[p.type] ?? p.type}</span>
          <p>{p.text}</p>
          {p.citations.length > 0 && (
            <ul className="citations">
              {p.citations.map((c: any) => (
                <li key={c.ref}>
                  <details>
                    <summary>{c.label}</summary>
                    <blockquote>{c.excerpt}</blockquote>
                  </details>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
